import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import "./PaymentSuccessful.css";
import { useNavigate } from "react-router-dom";
import CheckoutFooter from "../ComponentsCheckoutPage/Components/CheckoutFooter/CheckoutFooter";
import { CircularProgress } from "@mui/material";
import { NFOLogo, TickIcon } from "../../Assets/svgs";
import {
  verifyNFOTransaction,
  verifyPaymentStatusRazorpay,
} from "../../Utils/CheckoutAPIs/CheckoutAPIs";
import {
  getEventNameFromItemIds,
  trackData,
} from "../../Utils/attribution/CheckoutAttribution";
import {
  getRemoteConfig,
  getValue,
  fetchAndActivate,
} from "firebase/remote-config";
import { app } from "../../configuration";

import { CartContext } from "../../App";
import PaymentSuccessfulSummary from "./PaymentSuccessfulSummary";
import ReactGA from "react-ga4";
import { experimentTracker } from "../../Utils/ExperimentTracker";
import PFHBannerModal from "./Components/PFHBannerModal/PFHBannerModal";
import PFHBanner from "./Components/PFHBanner/PFHBanner";
import { isBundleLandingSchoolDetailsRequired } from "../../Utils/CommonMethods/bundleLandingHelpers";

export interface SummaryResponse {
  id: string;
  entity: string;
  amount: number;
  amount_paid: number;
  amount_due: number;
  currency: string;
  receipt: string;
  offer_id: null;
  status: string;
  attempts: number;
}

interface RemoteConfigData {
  enabled: boolean;
  title: string;
  subtitle: string;
  image_url: string;
  mobile_image_url: string;
  cta_text: string;
  cta_url: string;
  background_color: string;
  text_color: string;
  valid_from: string;
  valid_until: string;
}

const PaymentSuccessful = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const orderId = urlParams.get("order_id");
  const schoolCode = urlParams.get("scl");
  const [summaryResponse, setSummaryResponse] = useState<SummaryResponse>();

  const isInternationalTransaction = sessionStorage.getItem(
    "isInternationalTransaction"
  );
  let isInternational = false;
  try {
    isInternational = JSON.parse(isInternationalTransaction ?? "false");
  } catch (error) {
    console.error("Error parsing isInternationalTransaction:", error);
    isInternational = false;
  }
  const paymentType = sessionStorage.getItem("paymentType");

  const [alertToggle, setAlertToggle] = useState({
    alertMessage: "",
    isVisible: false,
  });

  const [pageLoading, setLoading] = useState(true);
  const [remoteConfig, setRemoteConfig] = useState<RemoteConfigData | null>(
    null
  );
  const [showBanner, setShowBanner] = useState(true);
  const [isConfigLoading, setIsConfigLoading] = useState(true);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [showModal, setShowModal] = useState(true);

  const verifyPaymentStripe = useCallback(async (orderId: string) => {
    try {
      // Create a Checkout Session
      const response = await fetch(
        "https://streak-payments.el.r.appspot.com/nfo/stripe-metadata",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sessionId: orderId,
          }),
        }
      );
      const data = await response.json();

      if (
        data.data.message ===
        "Metadata retrieved, student onboarded, and invoice sent successfully."
      ) {
        console.log("Payment Successful");
        setLoading(false);
      } else {
        navigate(`/CheckoutPage/?&scl=${schoolCode}`);
      }

      console.log(data.id);
      return data.clientSecret; // Assuming `data.id` is the client secret you need
    } catch (error) {
      navigate(`/CheckoutPage/?&scl=${schoolCode}`);
      // Handle the error accordingly
      return null; // Or throw error depending on your error handling strategy
    }
  }, []);

  const verifyPaymentBilldesk = async (orderId: string) => {
    try {
      const errorStatuses = [
        "payment_processing_error",
        "user_cancelled_error",
        "acquirer_processing_error",
        "risk_validation_error",
      ];

      const retryPayment = async (
        remainingRetries: number
      ): Promise<boolean> => {
        while (remainingRetries > 0) {
          const newResponse = await verifyNFOTransaction(orderId);
          console.log("This is new verify response after retry =", newResponse);
          //@ts-ignore
          const newPaymentStatus = newResponse.data.data.paymentStatus;
          if (newPaymentStatus === "success") {
            console.log("Payment Successful");
            return true;
          } else if (newPaymentStatus === "retry") {
            remainingRetries--;
            await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait for 1 second
          } else if (errorStatuses.includes(newPaymentStatus)) {
            console.log("Payment failed");
            return false;
          } else {
            return false;
          }
        }
        return false;
      };

      const response = await verifyNFOTransaction(orderId);
      console.log("This is the verify response", response);
      //@ts-ignore
      const paymentStatus = response.data.data.paymentStatus;
      if (paymentStatus === "success") {
        console.log("Payment Successful");
        return true;
      } else if (paymentStatus === "retry") {
        console.log("Retry payment");
        return await retryPayment(3);
      } else if (errorStatuses.includes(paymentStatus)) {
        console.log("Payment failed");
        return false;
      }
      return false;
    } catch (err) {
      console.log(err);
      return false;
    }
  };

  const { userDetails, nfoSelected } = useContext(CartContext);
  const bundleLandingRequiresDetails = useMemo(
    () => isBundleLandingSchoolDetailsRequired(),
    []
  );
  const nfoSelectedSession =
    typeof window !== "undefined"
      ? sessionStorage.getItem("nfoSelected")
      : null;
  const nfoSelectedBoolean = nfoSelectedSession === "true";
  const shouldCollectSchoolDetails =
    nfoSelected || nfoSelectedBoolean || bundleLandingRequiresDetails;
  const itemIds = sessionStorage.getItem("item_ids");

  const nfoSessionSelected = sessionStorage.getItem("nfoSelected") === "true";

  const verifyPayment = async (orderId: string) => {
    const paymentGateway = sessionStorage.getItem("pg");
    try {
      if (paymentGateway === "razorpay" && paymentType === "prepaid") {
        verifyPaymentStatusRazorpay(orderId)
          .then((response) => {
            if (response.status_code === 200) {
              if (
                response &&
                response.data &&
                response.data.status === "paid"
              ) {
                trackData(
                  nfoSelected
                    ? "user_completed_purchase_successfully_books+nfo"
                    : "user_completed_purchase_successfully",
                  null,
                  null,

                  {
                    success: true,
                    payment_type: "prepaid",
                    sale: {
                      product_type: getEventNameFromItemIds(itemIds ?? ""),
                      amount_inr: response.data.amount,
                      order_id: orderId,
                    },
                  }
                );
                ReactGA.event({
                  category: "user_completed_purchase_successfully",
                  action: "user_completed_purchase_successfully",
                  label: "user_completed_purchase_successfully",
                });

                // Experiment funnel step 4 - completion
                experimentTracker.trackPageView(
                  "/funnel/step4_purchase_complete",
                  "Purchase Complete"
                );
              } else {
                navigate(`/CheckoutPage/?&item_ids=${itemIds}`);
              }
              setLoading(false);
            } else {
              navigate(`/CheckoutPage/?&item_ids=${itemIds}`);
            }
          })
          .catch((error) => {
            navigate(`/CheckoutPage/?&item_ids=${itemIds}`);
          });
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.log(err);
      navigate(`/CheckoutPage/?&item_ids=${itemIds}`);
    }
  };

  useEffect(() => {
    setLoading(true);

    if (orderId) {
      if (isInternational) {
        verifyPaymentStripe(orderId);
      } else {
        verifyPayment(orderId);
      }
    } else {
      navigate(`/CheckoutPage/?&item_ids=${itemIds}`);
    }
  }, []);

  useEffect(() => {
    const fetchRemoteConfig = async () => {
      try {
        const remoteConfig = getRemoteConfig(app);
        remoteConfig.settings.minimumFetchIntervalMillis = 1000;
        await fetchAndActivate(remoteConfig);

        const bannerConfig = getValue(
          remoteConfig,
          "pfh_video_banner_upsell"
        ).asString();
        const parsedBanner = JSON.parse(bannerConfig);
        const { pfh_video_banner_upsell } = parsedBanner;

        console.log("this is the parsed banner", parsedBanner);
        setRemoteConfig(pfh_video_banner_upsell);
        setIsConfigLoading(false);
        console.log(
          "this is the remote config",
          pfh_video_banner_upsell.enabled
        );
      } catch (error) {
        console.error("Error fetching remote config:", error);
        setIsConfigLoading(false);
      }
    };

    fetchRemoteConfig();
  }, []);

  useEffect(() => {
    const hasSeenModal = sessionStorage.getItem("hasSeenPFHBanner");
    if (hasSeenModal === "true") {
      setShowModal(false);
    }
  }, []);

  const handleCloseModal = () => {
    setShowModal(false);
    sessionStorage.setItem("hasSeenPFHBanner", "true");
  };

  console.log("This is the summary response", summaryResponse);

  const sections = [
    {
      title: "Your Details",
      subTitle: (
        <>
          <div>
            <span style={{ color: "rgba(44, 106, 177, 0.8)" }}>
              {" "}
              First Name:{" "}
            </span>
            {userDetails?.firstName}
          </div>
          <div>
            <span style={{ color: "rgba(44, 106, 177, 0.8)" }}>
              {" "}
              Last Name:{" "}
            </span>
            {userDetails?.lastName}
          </div>
          <div>
            <span style={{ color: "rgba(44, 106, 177, 0.8)" }}> Email: </span>
            {userDetails?.email}
          </div>
          <div>
            <span style={{ color: "rgba(44, 106, 177, 0.8)" }}>
              {" "}
              Mobile Number:{" "}
            </span>
            {userDetails?.mobileNumber}
          </div>
          {shouldCollectSchoolDetails && (
            <>
              <div>
                <span style={{ color: "rgba(44, 106, 177, 0.8)" }}>
                  {" "}
                  Grade:{" "}
                </span>
                {userDetails?.grade}
              </div>
              <div>
                <span style={{ color: "rgba(44, 106, 177, 0.8)" }}>
                  {" "}
                  School:{" "}
                </span>
                {userDetails?.schoolName}
              </div>
            </>
          )}
        </>
      ),
      needsVerification: true,
    },
    {
      title: "Shipping Address",
      subTitle: (
        <>
          <div>{userDetails?.flatNo}</div>
          <div>{userDetails?.address}</div>
          <div>{userDetails?.city}</div>
          <div>{userDetails?.state}</div>
          <div>{userDetails?.pincode}</div>
        </>
      ),
      needsVerification: false,
    },

    {
      title: "Billing Address",
      subTitle: (
        <>
          <div>{userDetails?.flatNo}</div>
          <div>{userDetails?.address}</div>
          <div>{userDetails?.city}</div>
          <div>{userDetails?.state}</div>
          <div>{userDetails?.pincode}</div>
        </>
      ),
      needsVerification: false,
    },
    {
      title: "Shipping Method",
      subTitle: (
        <>
          <div>{"Standard Delivery"}</div>
          <div>7 - 8 Business Days</div>
        </>
      ),
      needsVerification: false,
    },
  ];

  const navigate = useNavigate();

  const handleImageClick = () => {
    trackData("user_clicked_banner", null, null);

    ReactGA.event({
      category: "user_clicked_banner",
      action: "user_clicked_banner",
      label: "user_clicked_banner",
    });

    if (remoteConfig?.cta_url) {
      // Create a copy of userDetails to avoid modifying the original
      const userDetailsToSend = {
        firstName: userDetails?.firstName || "",
        lastName: userDetails?.lastName || "",
        email: userDetails?.email || "",
        mobileNumber: userDetails?.mobileNumber || "",
        address: userDetails?.address || "",
        flatNo: userDetails?.flatNo || "",
        city: userDetails?.city || "",
        state: userDetails?.state || "",
        pincode: userDetails?.pincode || "",
        schoolName: userDetails?.schoolName || "",
        grade: userDetails?.grade || "",
      };

      // Convert to base64
      const base64UserDetails = btoa(JSON.stringify(userDetailsToSend));

      // Append to URL
      const url = new URL(remoteConfig.cta_url);
      url.searchParams.append("userDetails", base64UserDetails);

      window.open(url.toString(), "_blank");
    }
  };

  // Check if banner should be shown based on validity dates
  const isBannerValid = () => {
    if (!remoteConfig) return false;

    const now = new Date();
    const validFrom = new Date(remoteConfig.valid_from);
    const validUntil = new Date(remoteConfig.valid_until);

    return now >= validFrom && now <= validUntil;
  };

  const codPayment = paymentType === "cod";

  return pageLoading || isConfigLoading ? (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        width: "100vw",
      }}
    >
      <CircularProgress />
    </div>
  ) : (
    <div className="payment-successful-page">
      <img src={NFOLogo} className="nfo-logo" alt="NFO Logo" />
      <div className="line"></div>
      <div className="payment-success-container-parent">
        <div className="payment-success-container">
          <img src={TickIcon} className="tick-icon" alt="Success Tick" />
          <div className="successful-title">
            {nfoSelected || nfoSelectedBoolean
              ? "Your Order has been successfully placed."
              : " Thank you for purchasing NFO handbook."}
          </div>
          {!showBanner && !nfoSessionSelected && !codPayment && (
            <PFHBanner
              remoteConfig={remoteConfig}
              onImageClick={handleImageClick}
            />
          )}
          <PaymentSuccessfulSummary
            orderID={orderId ?? ""}
            sections={sections}
          />
        </div>
      </div>

      {!nfoSessionSelected && !codPayment && (
        <PFHBannerModal
          remoteConfig={remoteConfig}
          onClose={() => setShowBanner(false)}
          onImageClick={handleImageClick}
        />
      )}

      <CheckoutFooter />
    </div>
  );
};

export default PaymentSuccessful;
